'use client';

import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { useEffect, useRef } from 'react';

const RELOAD_FALLBACK_MS = 2000;

interface CapgoBootOverlayWindow extends Window {
  __capgoHideBootOverlay?: () => void;
}

export const CapgoOtaUpdate: React.FC = () => {
  const reloadFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const listenerHandles: PluginListenerHandle[] = [];

    const clearReloadFallback = () => {
      if (reloadFallbackTimerRef.current) {
        clearTimeout(reloadFallbackTimerRef.current);
        reloadFallbackTimerRef.current = null;
      }
    };

    const scheduleReloadFallback = () => {
      clearReloadFallback();
      reloadFallbackTimerRef.current = setTimeout(() => {
        void CapacitorUpdater.reload().catch(console.error);
      }, RELOAD_FALLBACK_MS);
    };

    const hideBootOverlay = () => {
      const bootWindow = window as CapgoBootOverlayWindow;

      bootWindow.__capgoHideBootOverlay?.();
    };

    const registerListeners = async () => {
      listenerHandles.push(
        await CapacitorUpdater.addListener('downloadComplete', () => {
          scheduleReloadFallback();
        })
      );

      listenerHandles.push(
        await CapacitorUpdater.addListener('set', () => {
          scheduleReloadFallback();
        })
      );

      listenerHandles.push(
        await CapacitorUpdater.addListener('appReloaded', () => {
          clearReloadFallback();
          hideBootOverlay();
        })
      );

      listenerHandles.push(
        await CapacitorUpdater.addListener('appReady', () => {
          clearReloadFallback();
          hideBootOverlay();
        })
      );
    };

    void registerListeners().catch(console.error);

    return () => {
      clearReloadFallback();
      void Promise.all(listenerHandles.map((handle) => handle.remove())).catch(
        console.error
      );
    };
  }, []);

  return null;
};
